<div class="api-docs">
  <div class="api-toolbar">
    <button class="print-btn" (click)="savePdf()">
      <i class="pi pi-download"></i>
      Save as PDF
    </button>
  </div>

  <!-- Sidebar -->
  <aside class="api-sidebar">
    <p class="sidebar-title">Contents</p>
    <nav>
      <ul>
        <li><a (click)="scrollTo('overview')" class="sidebar-link" (keydown.enter)="scrollTo('overview')" tabindex="0" role="button">Overview</a></li>
        <li><a (click)="scrollTo('authentication')" class="sidebar-link" (keydown.enter)="scrollTo('authentication')" tabindex="0" role="button">Authentication</a></li>
        <li class="sidebar-section">
          <a (click)="scrollTo('events-api')" class="sidebar-link sidebar-link--section" (keydown.enter)="scrollTo('events-api')" tabindex="0" role="button">Events API</a>
          <ul>
            <li>
              <a (click)="scrollTo('events-parameters')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('events-parameters')" tabindex="0" role="button"
                >Parameters</a
              >
            </li>
            <li>
              <a (click)="scrollTo('events-response')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('events-response')" tabindex="0" role="button">Response</a>
            </li>
            <li>
              <a (click)="scrollTo('events-example')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('events-example')" tabindex="0" role="button">Example</a>
            </li>
          </ul>
        </li>
        <li class="sidebar-section">
          <a (click)="scrollTo('passes-api')" class="sidebar-link sidebar-link--section" (keydown.enter)="scrollTo('passes-api')" tabindex="0" role="button">Passes API</a>
          <ul>
            <li>
              <a (click)="scrollTo('passes-parameters')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('passes-parameters')" tabindex="0" role="button"
                >Parameters</a
              >
            </li>
            <li>
              <a (click)="scrollTo('passes-areas')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('passes-areas')" tabindex="0" role="button"
                >Areas &amp; roles</a
              >
            </li>
            <li>
              <a (click)="scrollTo('passes-response')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('passes-response')" tabindex="0" role="button">Response</a>
            </li>
            <li>
              <a (click)="scrollTo('passes-example')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('passes-example')" tabindex="0" role="button">Example</a>
            </li>
          </ul>
        </li>
        <li class="sidebar-section">
          <a (click)="scrollTo('employees-api')" class="sidebar-link sidebar-link--section" (keydown.enter)="scrollTo('employees-api')" tabindex="0" role="button">Employees API</a>
          <ul>
            <li>
              <a (click)="scrollTo('employees-parameters')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('employees-parameters')" tabindex="0" role="button"
                >Extra Parameters</a
              >
            </li>
            <li>
              <a (click)="scrollTo('employees-example')" class="sidebar-link sidebar-link--sub" (keydown.enter)="scrollTo('employees-example')" tabindex="0" role="button"
                >Example</a
              >
            </li>
          </ul>
        </li>
        <li><a (click)="scrollTo('time-limitations')" class="sidebar-link" (keydown.enter)="scrollTo('time-limitations')" tabindex="0" role="button">Time Limitations</a></li>
      </ul>
    </nav>
  </aside>

  <!-- Main content -->
  <main class="api-main">
    <!-- Overview -->
    <section id="overview" class="api-section">
      <h1 class="api-page-title">External API Reference</h1>
      <p class="api-intro">
        The Yabi External API allows authorized third-party systems to create and manage events and access passes programmatically. All requests use <code>POST</code> with a JSON
        body and respond with JSON.
      </p>
      <div class="info-grid">
        <div class="info-row">
          <span class="info-label">Create passes</span>
          <span class="url-row">
            <code class="inline-code">POST {{ passesUrl }}</code>
            <button class="copy-btn" (click)="copyToClipboard(passesUrl, 'url-passes')">{{ copiedId === 'url-passes' ? 'Copied!' : 'Copy' }}</button>
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">Create events</span>
          <span class="url-row">
            <code class="inline-code">POST {{ eventsUrl }}</code>
            <button class="copy-btn" (click)="copyToClipboard(eventsUrl, 'url-events')">{{ copiedId === 'url-events' ? 'Copied!' : 'Copy' }}</button>
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">Content-Type</span>
          <code class="inline-code">application/json</code>
        </div>
        <div class="info-row">
          <span class="info-label">Test mode</span>
          <span
            >Append <code class="inline-code">/test</code> to either endpoint (e.g. <code class="inline-code">{{ passesTestUrl }}</code
            >). The request is validated in full, but nothing is saved and no SMS is sent.</span
          >
        </div>
      </div>

      <div class="callout callout--warning">
        <i class="callout__icon pi pi-exclamation-triangle"></i>
        <div>
          <strong>These are the only two addresses that serve this API.</strong>
          Yabi runs several services; the mobile app's API answers <code class="inline-code">404</code> for these paths, which looks like the endpoint no longer exists. If you get
          a 404, check the host in your request before anything else — copy the URL above rather than typing it.
        </div>
      </div>

      <div class="callout callout--warning">
        <i class="callout__icon pi pi-exclamation-triangle"></i>
        <div>
          <strong>Errors are returned with a 2xx status code.</strong>
          A rejected request still answers <code class="inline-code">200</code>; the outcome is in the body, never in the HTTP status. Always branch on
          <code>status</code> in the response — treating any 2xx as success will silently drop every rejection.
        </div>
      </div>

      <h3 class="api-subsection-title">Check your setup</h3>
      <p>Send this first. It is a dry run against the real endpoint — it proves the URL, the client ID and the token in one call, and creates nothing.</p>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Request</span>
          <button class="copy-btn" (click)="copyToClipboard(curlExample, 'curl')">
            {{ copiedId === 'curl' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ curlExample }}</pre>
      </div>
      <p>
        An empty <code>passes</code> array answers <code class="inline-code">[]</code> — that is a pass. Wrong credentials answer
        <code class="inline-code">{{ '{' }} "status": "error", "message": "invalid token or client id" {{ '}' }}</code
        >, and a wrong host answers a 404 page instead of JSON.
      </p>
    </section>

    <hr class="section-divider" />

    <!-- Authentication -->
    <section id="authentication" class="api-section">
      <h2 class="api-section-title">Authentication</h2>
      <p>
        Every request carries its credentials in the <strong>body</strong> — there is no
        <code>Authorization</code> header. A request is authenticated by <code>client_id</code> together with <code>token</code>, and by nothing else.
      </p>

      <div class="info-grid">
        <div class="info-row">
          <span class="info-label">Your <code>client_id</code></span>
          <span class="url-row">
            <code class="inline-code">{{ clientId }}</code>
            <button class="copy-btn" (click)="copyToClipboard(clientId, 'auth-client-id')">{{ copiedId === 'auth-client-id' ? 'Copied!' : 'Copy' }}</button>
          </span>
        </div>
        @if (canSeeToken) {
          <div class="info-row">
            <span class="info-label">Your <code>token</code></span>
            <span class="url-row">
              <code class="inline-code">{{ apiToken }}</code>
              <button class="copy-btn" (click)="copyToClipboard(apiToken, 'auth-token')">{{ copiedId === 'auth-token' ? 'Copied!' : 'Copy' }}</button>
            </span>
          </div>
        } @else {
          <div class="info-row">
            <span class="info-label">Your <code>token</code></span>
            <span>Shown in <strong>Settings &rarr; Business Details</strong> to users with access to that page.</span>
          </div>
        }
      </div>

      <table class="param-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th></th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>client_id</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Your organization's client ID, shown above. <code>/events</code> takes it as a number and <code>/passes</code> as a string; both endpoints accept either.
            </td>
          </tr>
          <tr>
            <td><code>token</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Your API token, from <strong>Settings &rarr; Business Details</strong>. Regenerating it there invalidates the old one immediately, for every integration using it.
            </td>
          </tr>
          <tr>
            <td><code>system_name</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              A label for the calling system, which <strong>you choose</strong>. It is not a credential and it is not stored on the pass — it identifies your system in Yabi's
              logs, so support can tell your traffic apart from everyone else's. Use one stable name per integration.
            </td>
          </tr>
        </tbody>
      </table>

      <div class="callout callout--info">
        <i class="callout__icon pi pi-info-circle"></i>
        <div>
          <strong><code>system_name</code> format:</strong>
          2–32 characters, letters, digits, <code class="inline-code">_</code> and <code class="inline-code">-</code>, starting with a letter or digit. It is upper-cased
          before it is recorded, so <code class="inline-code">my_system</code> and <code class="inline-code">MY_SYSTEM</code> are the same label. Anything else is rejected with
          <code class="inline-code">invalid system name</code> — including an unsubstituted template placeholder such as
          <code class="inline-code">{{ '{{' }}system_name{{ '}}' }}</code
          >, which is the most common cause of that error.
        </div>
      </div>

      <div class="callout callout--warning">
        <i class="callout__icon pi pi-exclamation-triangle"></i>
        <div>
          <strong>Do not send <code class="inline-code">MICROSOFT</code> or <code class="inline-code">WEWORK</code>.</strong>
          Those two names change behaviour rather than just labelling the caller — they override your <code>send_sms_new_pass</code> setting. Pick a name of your own.
        </div>
      </div>
    </section>

    <hr class="section-divider" />

    <!-- Events API -->
    <section id="events-api" class="api-section">
      <div class="endpoint-header">
        <span class="method-badge method-badge--post">POST</span>
        <code class="endpoint-url">/events</code>
      </div>
      <h2 class="api-section-title">Events API</h2>
      <p>Creates or updates an event. If an event with the same <code>event_id</code> already exists for the client, it will be updated. Otherwise a new event is created.</p>

      <h3 id="events-parameters" class="api-subsection-title">Parameters</h3>
      <table class="param-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th></th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>system_name</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>See <a (click)="scrollTo('authentication')" class="inline-link" (keydown.enter)="scrollTo('authentication')" tabindex="0" role="button">Authentication</a>.</td>
          </tr>
          <tr>
            <td><code>client_id</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>See <a (click)="scrollTo('authentication')" class="inline-link" (keydown.enter)="scrollTo('authentication')" tabindex="0" role="button">Authentication</a>.</td>
          </tr>
          <tr>
            <td><code>token</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>See <a (click)="scrollTo('authentication')" class="inline-link" (keydown.enter)="scrollTo('authentication')" tabindex="0" role="button">Authentication</a>.</td>
          </tr>
          <tr>
            <td><code>event_id</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Your system's own identifier for this event. Used to detect create vs. update. Only letters, digits, <code class="inline-code">_</code> and
              <code class="inline-code">-</code> are kept — any other character is <strong>silently removed</strong> before matching, so
              <code class="inline-code">EVT/2026#001</code> and <code class="inline-code">EVT2026001</code> are the same event.
            </td>
          </tr>
          <tr>
            <td><code>event_name</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>Display name of the event. Must be at least 5 characters after unsupported characters are removed, or the request is rejected.</td>
          </tr>
          <tr>
            <td><code>opening_time</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>ISO 8601 datetime when gates open (e.g. <code class="inline-code">2026-06-01T17:00:00</code>).</td>
          </tr>
          <tr>
            <td><code>starting_time</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>ISO 8601 datetime when the event starts.</td>
          </tr>
          <tr>
            <td><code>closing_time</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>ISO 8601 datetime when gates close.</td>
          </tr>
          <tr>
            <td><code>category</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Event category. Defaults to <code class="inline-code">"culture"</code>.</td>
          </tr>
          <tr>
            <td><code>event_password</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Event access password. Defaults to the client's configured default password.</td>
          </tr>
          <tr>
            <td><code>parent_id</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Internal Yabi event ID of the parent event. Use to create a sub-event.</td>
          </tr>
          <tr>
            <td><code>sub_event_areas</code></td>
            <td class="type-cell">String[]</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Array of area names for a sub-event. Required when <code class="inline-code">parent_id</code> is set.</td>
          </tr>
          <tr>
            <td>
              <code>predefined_location_id</code>
              <span class="badge badge--new">New</span>
            </td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Numeric ID of a predefined location. Preferred over <code class="inline-code">location</code> — use this when you know the location ID.</td>
          </tr>
          <tr>
            <td><code>allow_ticket_transfer</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              The <strong>string</strong> <code class="inline-code">"true"</code> or <code class="inline-code">"false"</code>. Omitting it, or sending the JSON boolean
              <code class="inline-code">true</code>, is rejected with <code class="inline-code">invalid allow ticket transfer value</code>.
            </td>
          </tr>
          <tr>
            <td><code>post_scan_transfer</code></td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Allow ticket transfer after the pass has been scanned.</td>
          </tr>
          <tr>
            <td><code>image</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Event image filename (lowercase).</td>
          </tr>
          <tr>
            <td><code>restrictions</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Access restriction rules for the event.</td>
          </tr>
        </tbody>
      </table>

      <h3 id="events-response" class="api-subsection-title">Response</h3>
      <table class="param-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>status</code></td>
            <td><code class="inline-code">"success"</code> or <code class="inline-code">"error"</code>.</td>
          </tr>
          <tr>
            <td><code>message</code></td>
            <td>Human-readable status or error description.</td>
          </tr>
          <tr>
            <td><code>event_number</code></td>
            <td>
              Yabi's own ID for the event. Present on success. <strong>Store it</strong> — this, not your <code>event_id</code>, is what
              <a (click)="scrollTo('passes-api')" class="inline-link" (keydown.enter)="scrollTo('passes-api')" tabindex="0" role="button">the Passes API</a> expects in each
              pass's <code>event_id</code> field.
            </td>
          </tr>
          <tr>
            <td>
              <code>monitor_link</code>
              <span class="badge badge--new">New</span>
            </td>
            <td>Direct URL to the event's live monitor screen. Present on success.</td>
          </tr>
        </tbody>
      </table>

      <h3 id="events-example" class="api-subsection-title">Example</h3>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Request</span>
          <button class="copy-btn" (click)="copyToClipboard(eventsExample, 'events-req')">
            {{ copiedId === 'events-req' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ eventsExample }}</pre>
      </div>
      <div class="code-block-wrapper code-block-wrapper--success">
        <div class="code-block-header">
          <span>Response — Success</span>
          <button class="copy-btn" (click)="copyToClipboard(eventsSuccessResponse, 'events-res-ok')">
            {{ copiedId === 'events-res-ok' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ eventsSuccessResponse }}</pre>
      </div>
      <div class="code-block-wrapper code-block-wrapper--error">
        <div class="code-block-header">
          <span>Response — Error</span>
          <button class="copy-btn" (click)="copyToClipboard(eventsErrorResponse, 'events-res-err')">
            {{ copiedId === 'events-res-err' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ eventsErrorResponse }}</pre>
      </div>
    </section>

    <hr class="section-divider" />

    <!-- Passes API -->
    <section id="passes-api" class="api-section">
      <div class="endpoint-header">
        <span class="method-badge method-badge--post">POST</span>
        <code class="endpoint-url">/passes</code>
      </div>
      <h2 class="api-section-title">Passes API</h2>
      <p>
        Creates or updates one or more access passes in a single request. Each pass object in the
        <code>passes</code> array is matched by <code>transaction</code> + <code>event_id</code> — if a matching pass exists it is updated, otherwise a new one is created.
      </p>
      <p>The top-level body contains the authentication fields plus the <code>passes</code> array.</p>

      <h3 class="api-subsection-title">Top-Level Fields</h3>
      <table class="param-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th></th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>system_name</code>, <code>client_id</code>, <code>token</code></td>
            <td class="type-cell">—</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>See <a (click)="scrollTo('authentication')" class="inline-link" (keydown.enter)="scrollTo('authentication')" tabindex="0" role="button">Authentication</a>.</td>
          </tr>
          <tr>
            <td><code>passes</code></td>
            <td class="type-cell">Object[]</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>The passes to create or update. Each entry uses the fields below.</td>
          </tr>
          <tr>
            <td><code>send_sms_new_pass</code></td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>
              Whether the holder is texted about the new pass. Defaults to <code class="inline-code">true</code>, and applies to the
              <strong>whole request</strong> — it cannot be set per pass. Set it to <code class="inline-code">false</code> while you are testing an integration against a
              real phone number.
            </td>
          </tr>
        </tbody>
      </table>

      <div class="callout callout--warning">
        <i class="callout__icon pi pi-exclamation-triangle"></i>
        <div>
          <strong><code>event_id</code> here is Yabi's event ID, not yours.</strong>
          The Events API matches on <em>your</em> <code>event_id</code> and answers with <code>event_number</code>; that number is what each pass's <code>event_id</code> must
          contain. Sending your own external code — or a guess like <code class="inline-code">"1"</code> — is rejected with
          <code class="inline-code">event not found</code>. You can also read the ID off the event in the portal.
        </div>
      </div>

      <h3 id="passes-parameters" class="api-subsection-title">Pass Parameters</h3>
      <table class="param-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th></th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>event_id</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Yabi's ID for the event — the <code>event_number</code> returned by the
              <a (click)="scrollTo('events-api')" class="inline-link" (keydown.enter)="scrollTo('events-api')" tabindex="0" role="button">Events API</a>, not your own event
              code.
            </td>
          </tr>
          <tr>
            <td><code>name</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Pass holder's first name. Unsupported characters are removed first; at least 2 must remain. (The rejection message says "minimum length is 5" — the rule it
              enforces is 2.)
            </td>
          </tr>
          <tr>
            <td><code>last_name</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Pass holder's last name. Required for employees.</td>
          </tr>
          <tr>
            <td><code>id_number</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>National ID number, or <code class="inline-code">"000000000"</code> (9 zeros) if not applicable. A real ID is required for employees.</td>
          </tr>
          <tr>
            <td><code>mobile</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>Mobile phone number.</td>
          </tr>
          <tr>
            <td><code>country_code</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Country dialing code without the <code class="inline-code">+</code> (e.g. <code class="inline-code">"972"</code>).</td>
          </tr>
          <tr>
            <td><code>email</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>Email address, or <code class="inline-code">"0@0.0"</code> if not available.</td>
          </tr>
          <tr>
            <td><code>transaction</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Your system's own identifier for this pass; repeating the same <code>transaction</code> for the same <code>event_id</code> updates that pass instead of creating
              another. Letters, digits and <code class="inline-code">_ - . , : ( ) [ ]</code> are kept and anything else is removed — at least 3 characters must remain.
            </td>
          </tr>
          <tr>
            <td><code>code</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Send <code class="inline-code">"_generate_"</code> and Yabi generates the access code and returns it. Any other value is used as the code itself (minimum 3
              characters) and is then yours to keep unique.
            </td>
          </tr>
          <tr>
            <td><code>allowed</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--required">Required</span></td>
            <td><code class="inline-code">1</code> to activate, <code class="inline-code">-1</code> to deactivate.</td>
          </tr>
          <tr>
            <td><code>secured</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>
              <code class="inline-code">0</code> — unsecured, <code class="inline-code">1</code> — security level 1, <code class="inline-code">2</code> —
              security level 2. If omitted, the pass inherits the event's level. <strong>This changes the response</strong> — see
              <a (click)="scrollTo('passes-response')" class="inline-link" (keydown.enter)="scrollTo('passes-response')" tabindex="0" role="button">Response</a>.
            </td>
          </tr>
          <tr>
            <td><code>areas</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--conditional">Conditional</span></td>
            <td>
              Comma-separated area names the pass grants access to (e.g. <code class="inline-code">"main entrance,vip"</code>). The names must already exist in the event.
              Required unless you send a <code>client_role</code> that carries areas — see
              <a (click)="scrollTo('passes-areas')" class="inline-link" (keydown.enter)="scrollTo('passes-areas')" tabindex="0" role="button">Areas and client roles</a>.
            </td>
          </tr>
          <tr>
            <td>
              <code>client_role</code>
              <span class="badge badge--new">New</span>
            </td>
            <td class="type-cell">Number | String</td>
            <td><span class="badge badge--conditional">Conditional</span></td>
            <td>
              The role to assign the pass, by ID or by name (case-insensitive). When the role carries areas, the pass inherits them — so that list does not have to be
              repeated on every request. Required for employees. See
              <a (click)="scrollTo('passes-areas')" class="inline-link" (keydown.enter)="scrollTo('passes-areas')" tabindex="0" role="button">Areas and client roles</a>.
            </td>
          </tr>
          <tr>
            <td><code>time_limitations</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              Stringified JSON defining the valid time range. See
              <a (click)="scrollTo('time-limitations')" class="inline-link" (keydown.enter)="scrollTo('time-limitations')" tabindex="0" role="button">Time Limitations</a>.
            </td>
          </tr>
          <tr>
            <td><code>transferable</code></td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Whether the pass can be transferred to another user. Always <code class="inline-code">false</code> for employees.</td>
          </tr>
          <tr>
            <td><code>open_once</code></td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Enables a remote open button on the pass. Defaults to <code class="inline-code">false</code>.</td>
          </tr>
          <tr>
            <td><code>single_uses</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Number of one-time uses. <code class="inline-code">0</code> means unlimited. Not applicable for employees.</td>
          </tr>
          <tr>
            <td><code>subareas</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Sub-area name within the specified area.</td>
          </tr>
          <tr>
            <td><code>row</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Seat row number (for ticketed venues).</td>
          </tr>
          <tr>
            <td><code>seat</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Seat number (for ticketed venues).</td>
          </tr>
          <tr>
            <td><code>category</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Pass category ID. Set to <code class="inline-code">10</code> for employees.</td>
          </tr>
          <tr>
            <td>
              <code>areas_add</code>
              <span class="badge badge--new">New</span>
            </td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>
              When present, switches this entry to <strong>area-update mode</strong> instead of pass create/update. <code class="inline-code">true</code> adds the
              <code>areas</code> to all matching passes; <code class="inline-code">false</code> removes them. Requires <code>mobile</code>, <code>country_code</code>,
              <code>event_id</code>, and <code>areas</code>. All other pass fields are ignored.
            </td>
          </tr>
        </tbody>
      </table>

      <h3 id="passes-areas" class="api-subsection-title">Areas and client roles</h3>
      <p>
        A pass has to say where its holder may go, and there are two ways to say it: <code>areas</code> on the pass, or a <code>client_role</code> that has areas assigned to it.
        One of the two is required.
      </p>
      <table class="param-table">
        <thead>
          <tr>
            <th>What you send</th>
            <th>What the pass gets</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>areas</code> only</td>
            <td>Those areas.</td>
          </tr>
          <tr>
            <td><code>client_role</code> only, and the role has areas</td>
            <td>The role's areas.</td>
          </tr>
          <tr>
            <td>Both</td>
            <td>Both sets combined. An area named in each is not added twice, and <code>areas</code> does not replace the role's own.</td>
          </tr>
          <tr>
            <td><code>client_role</code> only, and the role has no areas</td>
            <td>Rejected — <code class="inline-code">client role has no areas</code>.</td>
          </tr>
          <tr>
            <td>Neither</td>
            <td>Rejected — <code class="inline-code">missing argument</code>.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Events in the Sports &amp; Culture module are not held to this rule: areas are optional there, and an area name that does not exist yet is created rather than rejected.
        Everywhere else, an unknown area name is rejected with <code class="inline-code">area does not exist</code>, and a <code>client_role</code> that cannot be found with
        <code class="inline-code">role does not exist</code>.
      </p>

      <h3 class="api-subsection-title">Example — areas from a client role</h3>
      <p>No <code>areas</code> field: the role supplies them.</p>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Request</span>
          <button class="copy-btn" (click)="copyToClipboard(passByRoleExample, 'pass-role-req')">
            {{ copiedId === 'pass-role-req' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ passByRoleExample }}</pre>
      </div>

      <div class="callout callout--warning">
        <i class="callout__icon pi pi-exclamation-triangle"></i>
        <div>
          <strong>Attention — area-update mode (<code>areas_add</code>):</strong>
          When <code>areas_add</code> is set, the pass is identified by <code>mobile</code> + <code>country_code</code> rather than by <code>code</code>. This mode is for
          <strong>updating areas only</strong> — it does not create or modify any other pass fields. It will apply the change to <strong>all active and future passes</strong> for
          that mobile number in the specified event.
        </div>
      </div>

      <h3 class="api-subsection-title">Area-Update Example</h3>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Request — add areas by mobile</span>
          <button class="copy-btn" (click)="copyToClipboard(areasUpdateExample, 'areas-update-req')">
            {{ copiedId === 'areas-update-req' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ areasUpdateExample }}</pre>
      </div>

      <h3 id="passes-response" class="api-subsection-title">Response</h3>
      <p>Returns an array. It is <strong>not</strong> one entry per pass sent — see the rule below.</p>

      <div class="callout callout--warning">
        <i class="callout__icon pi pi-exclamation-triangle"></i>
        <div>
          <strong>A pass created with <code>secured</code> 1 or 2 produces no success entry.</strong>
          Its access code is delivered to the holder, not to you, so nothing is returned for it. A request in which every pass is secured and every pass succeeded therefore
          answers <code class="inline-code">[]</code>. An empty array means "all good and nothing to tell you", not "nothing happened" — failures are always listed, so a
          non-empty array is the thing to inspect.
        </div>
      </div>

      <table class="param-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>status</code></td>
            <td><code class="inline-code">"success"</code> or <code class="inline-code">"error"</code>.</td>
          </tr>
          <tr>
            <td><code>message</code></td>
            <td>Why the pass was rejected. Present on <code class="inline-code">"error"</code> entries only.</td>
          </tr>
          <tr>
            <td><code>code</code></td>
            <td>
              The pass's access code. Present only when the pass is <strong>unsecured</strong> (<code class="inline-code">secured: 0</code>); when you sent
              <code class="inline-code">"_generate_"</code> this is where the generated code comes back. On an error entry it is present only if you supplied the code yourself.
            </td>
          </tr>
          <tr>
            <td><code>mobile</code></td>
            <td>Mobile number of the pass holder. Returned when the code was generated for you, so the entry can be matched to a person.</td>
          </tr>
          <tr>
            <td><code>country_code</code></td>
            <td>Country code of the pass holder. Returned alongside <code>mobile</code>.</td>
          </tr>
          <tr>
            <td><code>passes_updated</code></td>
            <td>Number of passes whose areas were modified. Present only for area-update mode entries.</td>
          </tr>
        </tbody>
      </table>

      <div class="callout callout--info">
        <i class="callout__icon pi pi-info-circle"></i>
        <div>
          Passes are matched by <code>transaction</code> + <code>event_id</code>, so a request that fails can be sent again unchanged once corrected — the passes that
          already succeeded are updated, not duplicated.
        </div>
      </div>

      <h3 id="passes-example" class="api-subsection-title">Example</h3>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Request</span>
          <button class="copy-btn" (click)="copyToClipboard(passExample, 'pass-req')">
            {{ copiedId === 'pass-req' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ passExample }}</pre>
      </div>
      <div class="code-block-wrapper code-block-wrapper--success">
        <div class="code-block-header">
          <span>Response — Success (unsecured pass)</span>
          <button class="copy-btn" (click)="copyToClipboard(passSuccessResponse, 'pass-res')">
            {{ copiedId === 'pass-res' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ passSuccessResponse }}</pre>
      </div>
      <div class="code-block-wrapper code-block-wrapper--success">
        <div class="code-block-header">
          <span>Response — Success (secured pass)</span>
          <button class="copy-btn" (click)="copyToClipboard(passSecuredResponse, 'pass-res-secured')">
            {{ copiedId === 'pass-res-secured' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ passSecuredResponse }}</pre>
      </div>
      <div class="code-block-wrapper code-block-wrapper--error">
        <div class="code-block-header">
          <span>Response — Error (still HTTP 200)</span>
          <button class="copy-btn" (click)="copyToClipboard(passErrorResponse, 'pass-res-err')">
            {{ copiedId === 'pass-res-err' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ passErrorResponse }}</pre>
      </div>
    </section>

    <hr class="section-divider" />

    <!-- Employees API -->
    <section id="employees-api" class="api-section">
      <div class="endpoint-header">
        <span class="method-badge method-badge--post">POST</span>
        <code class="endpoint-url">/passes</code>
        <span class="endpoint-note">— same endpoint, category: 10</span>
      </div>
      <h2 class="api-section-title">Employees API</h2>
      <p>
        Employees are created through the same <code>/passes</code> endpoint with <code>category: 10</code>. All
        <a (click)="scrollTo('passes-parameters')" class="inline-link" (keydown.enter)="scrollTo('passes-parameters')" tabindex="0" role="button">pass parameters</a> apply, plus
        the additional fields below.
      </p>
      <div class="callout callout--info">For employees, <code>time_limitations</code> range end must always be <code class="inline-code">0000000000</code> (unlimited).</div>

      <h3 id="employees-parameters" class="api-subsection-title">Additional Employee Parameters</h3>
      <table class="param-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th></th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>wage_agreement</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>ID of the employee's wage agreement (must exist in your client configuration).</td>
          </tr>
          <tr>
            <td><code>client_role</code></td>
            <td class="type-cell">Number | String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              The employee's role, by ID or by name, and it must be a role in the <strong>employees</strong> category. As everywhere else, the role's areas are added to the
              pass — see <a (click)="scrollTo('passes-areas')" class="inline-link" (keydown.enter)="scrollTo('passes-areas')" tabindex="0" role="button">Areas and client
              roles</a>.
            </td>
          </tr>
          <tr>
            <td><code>date_of_birth</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>Format: <code class="inline-code">DD-MM-YYYY</code> (e.g. <code class="inline-code">"15-03-1985"</code>).</td>
          </tr>
          <tr>
            <td><code>address</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>Employee's physical address.</td>
          </tr>
          <tr>
            <td><code>family_status</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--required">Required</span></td>
            <td>
              One of: <code class="inline-code">"married"</code>, <code class="inline-code">"single"</code>, <code class="inline-code">"divorced"</code>,
              <code class="inline-code">"widowed"</code>, <code class="inline-code">"separated"</code>, <code class="inline-code">"not_relevant"</code>.
            </td>
          </tr>
          <tr>
            <td><code>gender</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>One of: <code class="inline-code">"male"</code>, <code class="inline-code">"female"</code>, <code class="inline-code">"not_relevant"</code>.</td>
          </tr>
          <tr>
            <td><code>personal_id</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Wage system or third-party identification number.</td>
          </tr>
          <tr>
            <td><code>day_off</code></td>
            <td class="type-cell">String</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Comma-separated rest days. 1=Sun, 2=Mon, 3=Tue, 4=Wed, 5=Thu, 6=Fri, 7=Sat. Example: <code class="inline-code">"6,7"</code> for Friday &amp; Saturday.</td>
          </tr>
          <tr>
            <td><code>remote_attendance</code></td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Allow the employee to mark attendance remotely. Defaults to <code class="inline-code">false</code>.</td>
          </tr>
          <tr>
            <td><code>attendance_manager</code></td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Allow the employee to manage other employees' attendance. Defaults to <code class="inline-code">false</code>.</td>
          </tr>
          <tr>
            <td><code>create_pass</code></td>
            <td class="type-cell">Boolean</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Allow the employee to create passes for others. Defaults to <code class="inline-code">false</code>.</td>
          </tr>
          <tr>
            <td><code>create_pass_limit</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Maximum number of passes the employee can create. Only applicable when <code class="inline-code">create_pass</code> is <code class="inline-code">true</code>.</td>
          </tr>
          <tr>
            <td><code>create_pass_max_period</code></td>
            <td class="type-cell">Number</td>
            <td><span class="badge badge--optional">Optional</span></td>
            <td>Maximum number of days in advance the employee can create passes.</td>
          </tr>
        </tbody>
      </table>

      <h3 id="employees-example" class="api-subsection-title">Example</h3>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Request</span>
          <button class="copy-btn" (click)="copyToClipboard(employeeExample, 'emp-req')">
            {{ copiedId === 'emp-req' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ employeeExample }}</pre>
      </div>
    </section>

    <hr class="section-divider" />

    <!-- Time Limitations -->
    <section id="time-limitations" class="api-section">
      <h2 class="api-section-title">Time Limitations</h2>
      <p>
        The <code>time_limitations</code> field is a <strong>stringified JSON object</strong> that controls when a pass is valid. Unix timestamps (seconds) are used for all time
        values.
      </p>

      <h3 class="api-subsection-title">Basic range</h3>
      <p>Defines a simple start–end window.</p>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Example</span>
          <button class="copy-btn" (click)="copyToClipboard(timeLimitationsBasic, 'tl-basic')">
            {{ copiedId === 'tl-basic' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ timeLimitationsBasic }}</pre>
      </div>

      <h3 class="api-subsection-title">Range with daily hour windows</h3>
      <p>Restrict access to specific hours on specific days within the range. Day keys: 1=Sunday … 7=Saturday.</p>
      <div class="code-block-wrapper">
        <div class="code-block-header">
          <span>Example</span>
          <button class="copy-btn" (click)="copyToClipboard(timeLimitationsAdvanced, 'tl-adv')">
            {{ copiedId === 'tl-adv' ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="code-block">{{ timeLimitationsAdvanced }}</pre>
      </div>

      <table class="param-table" style="margin-top: 24px">
        <thead>
          <tr>
            <th>Rule</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Range end &gt; range start</td>
            <td>Always required.</td>
          </tr>
          <tr>
            <td>Unlimited end</td>
            <td>Use <code class="inline-code">0000000000</code> (10 zeros). Required for employees.</td>
          </tr>
          <tr>
            <td>Start limit</td>
            <td>Start time must not be more than 30 years in the past.</td>
          </tr>
          <tr>
            <td>Hour format</td>
            <td><code class="inline-code">HHMM</code> — e.g. <code class="inline-code">"0900"</code> for 09:00, <code class="inline-code">"2359"</code> for 23:59.</td>
          </tr>
        </tbody>
      </table>
    </section>
  </main>
</div>
